import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Pencil, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useDecideApproval } from '../../../hooks/mutations';
import {
  CardRow, CardShell, ResolvedCard, ghostBtn, inputCls, outcomeOf, parseResult, primaryBtn, resultError, secondaryBtn,
} from './CardShell';
import { newChatPath } from '../chatLinks';

const Persona = ({ text }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const long = (text || '').length > 240;
  if (!text) return null;
  return (
    <div className="mt-1.5 rounded-lg bg-muted/60 px-3 py-2 text-[13px] text-foreground/85 whitespace-pre-wrap break-words">
      {long && !all ? `${text.slice(0, 240)}...` : text}
      {long && (
        <button type="button" onClick={() => setAll((v) => !v)} className="ml-1 text-xs text-amber-700 dark:text-amber-400 hover:underline">
          {t(all ? 'bots.cards.show_less' : 'bots.cards.show_more')}
        </button>
      )}
    </div>
  );
};

/**
 * BusyBee's main chat wants to make a new bot (or change one, agents/cards.py bot_card): its
 * name, what it does and its instructions. The person confirms, edits first, or says no.
 */
const BotCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  const card = approval.card;
  const changes = card.changes || {};
  const shown = {
    name: changes.name || card.name, description: changes.description ?? card.description ?? '',
    persona: changes.persona ?? card.persona ?? '',
  };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shown);

  if (approval.status !== 'pending') {
    const done = approval.status === 'approved';
    const error = resultError(approval);
    const made = parseResult(approval);
    const final = { ...shown, ...(approval.edits || {}) };
    const botId = made?.id || card.bot_id;
    return (
      <ResolvedCard icon={Sparkles} testid="bot-card-resolved" ok={done && !error}
        text={final.name}
        outcome={error ? t('bots.cards.failed') : outcomeOf(approval, t, t(`bots.cards.bot_done_${card.action}`))}
        action={done && !error && botId ? (
          <Link to={newChatPath({ botId })} className="flex-shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title={t('bots.hub.chat')} aria-label={t('bots.hub.chat')} data-testid="bot-card-open">
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : null} />
    );
  }

  const busy = decide.isPending;
  const send = (approve) => {
    const edits = editing && approve ? Object.fromEntries(
      ['name', 'description', 'persona'].filter((k) => draft[k] !== shown[k]).map((k) => [k, draft[k]])) : undefined;
    decide.mutate({ approvalId: approval.id, approve, edits: edits && Object.keys(edits).length ? edits : undefined }, {
      onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
    });
  };
  const changed = (key) => card.action === 'update' && key in changes;

  return (
    <CardShell icon={Sparkles} testid="bot-card" waiting compact={compact}
      eyebrow={t(`bots.cards.bot_eyebrow_${card.action}`, { bot: botName })}
      title={editing ? null : shown.name}
      footer={(
        <>
          {!editing && (
            <button type="button" className={secondaryBtn} onClick={() => { setDraft(shown); setEditing(true); }} disabled={busy}
              data-testid="bot-card-edit">
              <Pencil className="w-3.5 h-3.5" />{t('bots.cards.edit')}
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={() => send(false)} disabled={busy} data-testid="card-decline">
            {t('bots.cards.not_now')}
          </button>
          <button type="button" className={primaryBtn} onClick={() => send(true)}
            disabled={busy || (editing && !draft.name.trim())} data-testid="card-confirm">
            {t(`bots.cards.bot_confirm_${card.action}`)}
          </button>
        </>
      )}>
      {editing ? (
        <div className="space-y-2.5 pb-1">
          <input value={draft.name} maxLength={60} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className={inputCls} aria-label={t('bots.cards.bot_name')} data-testid="bot-card-name" />
          <input value={draft.description} maxLength={200} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            className={inputCls} aria-label={t('bots.cards.bot_description')} placeholder={t('bots.cards.bot_description')} />
          <textarea value={draft.persona} maxLength={4000} rows={6}
            onChange={(e) => setDraft((d) => ({ ...d, persona: e.target.value }))}
            className={`${inputCls} h-auto py-2 resize-y`} aria-label={t('bots.cards.bot_persona')} />
        </div>
      ) : (
        <div>
          {card.team_name && <CardRow label={t('bots.cards.bot_team')}>{card.team_name}</CardRow>}
          {changed('name') && (
            <CardRow label={t('bots.cards.bot_name')}>
              <span><span className="line-through text-muted-foreground mr-1.5">{card.name}</span>{changes.name}</span>
            </CardRow>
          )}
          {shown.description && (
            <CardRow label={t('bots.cards.bot_description')}>{shown.description}</CardRow>
          )}
          {(card.action === 'create' || changed('persona')) && <Persona text={shown.persona} />}
        </div>
      )}
    </CardShell>
  );
};

export default BotCard;
